<template>
  <div>
    <div class="flex justify-between items-center q-mb-lg">
      <h3>Användare</h3>
      <div class="text-caption text-grey-7">
        {{ users.length }} träffar
      </div>
    </div>

    <div class="filters q-mb-md">
      <q-input
        v-model="search"
        label="Sök på namn eller e-post"
        outlined
        dense
        clearable
        debounce="300"
        @update:model-value="fetchUsers"
        class="filter-search"
      />
      <q-select
        v-model="roleFilter"
        :options="roleOptions"
        label="Filtrera på roll"
        outlined
        dense
        clearable
        emit-value
        map-options
        @update:model-value="fetchUsers"
        class="filter-role"
      />
    </div>

    <q-table
      :rows="users"
      :columns="columns"
      row-key="_id"
      :loading="loading"
      :pagination="{ rowsPerPage: 50 }"
    >
      <template v-slot:body-cell-name="props">
        <q-td :props="props">
          {{ props.row.name || "—" }}
        </q-td>
      </template>

      <template v-slot:body-cell-roles="props">
        <q-td :props="props">
          <q-badge
            v-for="role in props.row.roles"
            :key="role"
            :color="roleColor(role)"
            class="q-mr-xs"
          >
            {{ role }}
          </q-badge>
        </q-td>
      </template>

      <template v-slot:body-cell-venue="props">
        <q-td :props="props">
          {{ props.row.venue || "—" }}
        </q-td>
      </template>

      <template v-slot:body-cell-providers="props">
        <q-td :props="props">
          <span
            v-for="p in props.row.providers"
            :key="p.provider"
            class="provider-badge"
          >
            {{ p.provider }}
          </span>
        </q-td>
      </template>

      <template v-slot:body-cell-edit="props">
        <q-td :props="props" class="text-center">
          <q-btn
            icon="edit"
            color="primary"
            flat
            dense
            @click="openEdit(props.row)"
          />
        </q-td>
      </template>
    </q-table>

    <!-- Edit-dialog -->
    <q-dialog v-model="isEditOpen" persistent>
      <q-card style="width: 540px; max-width: 90vw">
        <q-card-section>
          <div class="text-h6">Redigera användare</div>
          <div v-if="editing" class="text-caption text-grey-7 q-mt-xs">
            {{ editing.email }}
          </div>
        </q-card-section>

        <q-card-section>
          <q-form @submit.prevent="saveEdit">
            <q-option-group
              v-model="form.roles"
              :options="roleAssignOptions"
              type="checkbox"
              class="q-mb-md"
            />

            <q-input
              v-model="form.venue"
              label="Venue (krävs för organizer-roll)"
              outlined
              :error="venueMissingForOrganizer"
              :error-message="venueMissingForOrganizer ? 'Organizer-rollen kräver en venue.' : ''"
              hint="Lämna tomt för vanliga användare och super-admins"
              class="q-mb-md"
            />

            <div class="flex justify-end q-gutter-sm q-mt-md">
              <q-btn flat label="Avbryt" color="grey-7" @click="closeEdit" />
              <q-btn
                label="Spara"
                type="submit"
                color="primary"
                :loading="saving"
                :disable="venueMissingForOrganizer"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useQuasar } from "quasar";
import axios from "axios";
import { useRouter } from "vue-router";
import { clearAuth } from "@/utils/auth";

export default {
  name: "AdminUsers",
  setup() {
    const $q = useQuasar();
    const router = useRouter();

    const users = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const search = ref("");
    const roleFilter = ref(null);

    const isEditOpen = ref(false);
    const editing = ref(null);
    const form = ref({ roles: [], venue: "" });

    // Organizer-rollen kräver en venue för att events ska kunna kopplas
    // till någon lokal. Backend rejectar samma kombination med 400.
    const venueMissingForOrganizer = computed(() =>
      form.value.roles.includes("organizer") && !form.value.venue.trim()
    );

    const roleOptions = [
      { label: "Alla", value: null },
      { label: "Vanlig användare", value: "user" },
      { label: "Arrangör", value: "organizer" },
      { label: "Super-admin", value: "super-admin" },
    ];

    const roleAssignOptions = [
      { label: "Vanlig användare (basroll)", value: "user" },
      { label: "Arrangör (kan skapa egna events)", value: "organizer" },
      { label: "Super-admin (full åtkomst)", value: "super-admin" },
    ];

    const columns = [
      { name: "email", label: "E-post", field: "email", align: "left", sortable: true },
      { name: "name", label: "Namn", field: "name", align: "left" },
      { name: "roles", label: "Roller", align: "left" },
      { name: "venue", label: "Venue", field: "venue", align: "left" },
      { name: "providers", label: "Login-metod", align: "left" },
      { name: "edit", label: "", align: "center" },
    ];

    const roleColor = (role) => {
      switch (role) {
        case "super-admin": return "primary";
        case "organizer": return "dark";
        case "user": return "grey-6";
        default: return "grey";
      }
    };

    const handleAuthError = (err) => {
      const status = err.response?.status;
      if (status === 401) {
        clearAuth();
        router.push("/login");
        return true;
      }
      if (status === 403) {
        $q.notify({
          type: "negative",
          message: "Du har inte behörighet för den här vyn.",
        });
        router.push("/admin");
        return true;
      }
      return false;
    };

    const fetchUsers = async () => {
      loading.value = true;
      try {
        const token = localStorage.getItem("token");
        const params = {
          search: search.value || undefined,
          role: roleFilter.value || undefined,
          limit: 200,
        };
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "admin/users",
          { headers: { Authorization: `Bearer ${token}` }, params }
        );
        users.value = data;
      } catch (err) {
        if (!handleAuthError(err)) {
          $q.notify({ type: "negative", message: "Kunde inte hämta användare" });
        }
      } finally {
        loading.value = false;
      }
    };

    const openEdit = (row) => {
      editing.value = row;
      form.value = {
        roles: [...(row.roles || [])],
        venue: row.venue || "",
      };
      isEditOpen.value = true;
    };

    const closeEdit = () => {
      isEditOpen.value = false;
      editing.value = null;
    };

    const saveEdit = async () => {
      if (!editing.value) return;
      saving.value = true;
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.patch(
          process.env.VUE_APP_API_URL +
            "admin/users/" +
            editing.value._id,
          {
            roles: form.value.roles.length ? form.value.roles : ["user"],
            venue: form.value.venue || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const idx = users.value.findIndex((u) => u._id === data._id);
        if (idx !== -1) users.value[idx] = data;

        $q.notify({ type: "positive", message: "Sparat!" });
        closeEdit();
      } catch (err) {
        if (!handleAuthError(err)) {
          $q.notify({
            type: "negative",
            message: err.response?.data?.message || "Kunde inte spara",
          });
        }
      } finally {
        saving.value = false;
      }
    };

    onMounted(() => {
      fetchUsers();
    });

    return {
      users,
      loading,
      saving,
      search,
      roleFilter,
      roleOptions,
      roleAssignOptions,
      columns,
      isEditOpen,
      editing,
      form,
      venueMissingForOrganizer,
      roleColor,
      fetchUsers,
      openEdit,
      closeEdit,
      saveEdit,
    };
  },
};
</script>

<style scoped>
.filters {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}
.filter-search { min-width: 280px; flex: 1; }
.filter-role { min-width: 200px; }
.provider-badge {
  display: inline-block;
  padding: 1px 8px;
  /* Använd sajtens CSS-variabler så badge:n adapterar i dark mode -
     hardcoded grey-3 var oläsbar mot ljus text. */
  background: var(--color-border);
  color: var(--color-text-secondary);
  border-radius: 999px;
  font-size: 0.7rem;
  margin-right: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
